import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import {
  AssetManagementService,
  ProcessedAssetData,
  CardType,
} from '../asset-management-service';

import { DateUtils } from '@app/shared/utils/date.utils';
import { Subject, takeUntil } from 'rxjs';
import { formatSingleDate, generateLast12Months } from '@utils/utils';
import { yearDateFormat } from '@constants/dateFormat';
import {
  LocationDeviceRow,
} from '../asset-management.config';
import { GlobalErrorService } from '@services/global-error.service';
import { ErrorMessages } from '@app/shared/constants/constants';
import { AssetAllocatedTableService } from '../asset-management-table/asset-allocated-table.service';

@Component({
  selector: 'app-asset-allocated',
  templateUrl: './asset-allocated.component.html',
  styleUrls: ['./asset-allocated.component.scss'],
})
export class AssetAllocatedComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  public chartWidth = 500;
  public chartHeight = 250;
  public allocationData: ProcessedAssetData[] = [];
  public isLoading = false;
  imageSrc: string = 'assets/img/no-data-found.png';
  errorMsg: string = ErrorMessages.NoDataAvailable;

  public assetTotals: { asset: string; total: number }[] = [];
  @Input() chartType: 'bar' | 'line' | 'area' | 'pie' | 'doughnut' = 'bar';

  selectedMonthLabel: string | null = null;
  selectedMonthValue: string = `${new Date().getMonth() + 1}-${new Date().getFullYear()}`;
  monthOptions = generateLast12Months();
  monthSelected: string = '';
  yearSelected: string = '';
  // Brand colors defined locally (not global)
  // private readonly MACBOOK_COLOR = '#D5710C'; // $lightest-orange
  // private readonly WINDOWS_COLOR = '#4C9E8C'; // $sky-green
  // private readonly COLOR_MAP: Record<string, string> = {
  //   macbook: this.MACBOOK_COLOR,
  //   windows: this.WINDOWS_COLOR,
  // };

  private readonly MACBOOK_ASSET_LABEL = 'MacBook';
  private readonly WINDOWS_ASSET_LABEL = 'Windows';

  constructor(
    private readonly assetManagementService: AssetManagementService,
    private readonly router: Router,
    private readonly errorHandler: GlobalErrorService,
    private readonly assetAllocatedTableService: AssetAllocatedTableService,
  ) {}

  ngOnInit(): void {
    this.assetManagementService.preloadAccessoriesData().catch(error => {
    });

    this.assetAllocatedTableService.loading$
      .pipe(takeUntil(this.destroy$))
      .subscribe(loading => {
        this.isLoading = loading;
      });

    const currentDate = new Date();
    this.selectedMonthValue = `${currentDate.getMonth() + 1}-${currentDate.getFullYear()}`;
    const startOfMonth = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      1,
    );
    this.monthSelected = DateUtils.getMonthName(currentDate.getMonth());
    this.yearSelected = currentDate.getFullYear().toString();
    this.loadAssetAllocationData(
      formatSingleDate(startOfMonth, yearDateFormat.date),
      formatSingleDate(currentDate, yearDateFormat.date),
    );
  }

  get chartData(): ProcessedAssetData[] {
    return this.allocationData;
  }
  get xKeyForChart(): string {
    return 'location';
  }
  get yKeysForChart(): string[] {
    return ['macbook', 'windows'];
  }
  get labelKeyForChart(): string {
    return '';
  }
  get valueKeyForChart(): string {
    return '';
  }
  // get colorMapForChart(): Record<string, string> {
  //   return this.COLOR_MAP;
  // }

  onMonthSelect(event: any): void {
    this.selectedMonthLabel = event;
    if (!event) return;
    this.selectedMonthValue = event;
    const [monthStr, yearStr] = event.split('-');
    const monthIndex = Number(monthStr) - 1;
    const year = Number(yearStr);
    const startOfMonth = new Date(year, monthIndex, 1);
    const endOfMonth = new Date(year, monthIndex + 1, 0);
    this.loadAssetAllocationData(
      formatSingleDate(startOfMonth, yearDateFormat.date),
      formatSingleDate(endOfMonth, yearDateFormat.date),
    );
  }

  navigateToAssetDetails(): void {
    localStorage.setItem('selectedCard', 'asset-allocated');
    this.router.navigate(['/asset-management/asset-allocated'], {
      queryParams: { card: 'asset-allocated' },
      state: { selectedMonth: this.selectedMonthValue }
    });
  }

  navigateToMyRequests(cardType: CardType = 'header'): void {
    localStorage.setItem('selectedCard', cardType);
    this.router.navigate(['/asset-management/asset-allocated']);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadAssetAllocationData(startDate: string, endDate: string): void {
    if (!startDate || !endDate) {
      return;
    }

    const date = new Date(startDate);
    const month = DateUtils.getMonthName(date.getMonth());
    const year = date.getFullYear().toString();

    this.isLoading = true;

    this.assetManagementService
      .getAssetAllocationData(month, year)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.isLoading = false;
          if (response.status === 'SUCCESS') {
            this.allocationData =
              this.assetManagementService.processAssetAllocationData(
                response.data,
              );
            this.assetTotals = this.computeAssetTotals(this.allocationData);
          }
        },
        error: (error: any) => {
          this.isLoading = false;
          this.errorHandler.logComponentError(error);
        },
      });
  }


  private computeAssetTotals(
    data: ProcessedAssetData[],
  ): { asset: string; total: number }[] {
    const macbookTotal = data.reduce((sum, row) => sum + row.macbook, 0);
    const windowsTotal = data.reduce((sum, row) => sum + row.windows, 0);
    return [
      { asset: this.MACBOOK_ASSET_LABEL, total: macbookTotal },
      { asset: this.WINDOWS_ASSET_LABEL, total: windowsTotal },
    ];
  }
}
